<template>
  <base-view id="support-portal-article">
    <div class="w-full page-fill bg-gray-lighter">
      <div class="flex flex-col items-center px-6 pt-10 pb-16">
        <!-- Error / loading / not-found: narrow centered -->
        <div
          v-if="zendeskError || loading || !article"
          class="w-full max-w-3xl"
        >
          <div v-if="zendeskError" class="text-center">
            <p class="mb-4 text-gray-700">
              We couldn't load this article. Please check your connection and
              try again.
            </p>
            <button
              type="button"
              @click="fetchZendeskData"
              class="px-5 py-2 text-sm font-bold text-white rounded-full bg-primary"
            >
              Try again
            </button>
          </div>
          <div v-else-if="loading" class="animate-pulse">
            <div class="w-2/3 h-4 mb-6 bg-gray-200 rounded"></div>
            <div class="w-full h-8 mb-7 bg-gray-200 rounded"></div>
            <div class="flex flex-col gap-4">
              <div class="w-full h-4 bg-gray-200 rounded"></div>
              <div class="w-full h-4 bg-gray-200 rounded"></div>
              <div class="w-5/6 h-4 bg-gray-200 rounded"></div>
              <div class="w-full h-32 bg-gray-200 rounded"></div>
              <div class="w-3/4 h-4 bg-gray-200 rounded"></div>
            </div>
          </div>
          <div v-else class="flex flex-col items-center py-12 text-center">
            <div
              class="flex items-center justify-center w-24 h-24 mb-6 rounded-full bg-primary-50"
            >
              <svg
                width="44"
                height="44"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#096843"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <path
                  d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"
                />
                <polyline points="14 2 14 8 20 8" />
                <line x1="12" y1="11" x2="12" y2="11.01" />
                <line x1="12" y1="14" x2="12" y2="17" />
              </svg>
            </div>
            <h2 class="mb-2 text-2xl font-extrabold text-gray-900">
              Article not found
            </h2>
            <p class="max-w-sm mb-8 text-gray-600">
              This article may have been moved or renamed. Try searching for
              what you need — it's probably still here.
            </p>
            <div class="flex flex-wrap justify-center gap-3">
              <router-link
                to="/support-portal"
                class="px-6 py-2.5 text-sm font-bold text-white rounded-full bg-primary hover:bg-primary-mid transition-colors"
                >Search Support</router-link
              >
              <button
                type="button"
                @click="$router.go(-1)"
                class="px-6 py-2.5 text-sm font-bold text-gray-700 bg-white border border-gray-300 rounded-full hover:border-primary hover:text-primary transition-colors"
              >
                Go Back
              </button>
            </div>
          </div>
        </div>

        <!-- Article loaded -->
        <div v-else class="w-full max-w-3xl">
          <button
            v-if="backLabel"
            type="button"
            @click="$router.go(-1)"
            class="inline-flex items-center gap-1.5 mb-4 text-sm font-semibold text-gray-500 hover:text-primary transition-colors"
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path d="M19 12H5M12 5l-7 7 7 7" />
            </svg>
            {{ backLabel }}
          </button>
          <div
            class="flex flex-wrap items-center justify-between gap-2 mb-6 text-sm"
          >
            <div class="flex flex-wrap items-center gap-2">
              <router-link
                to="/support-portal"
                class="font-semibold text-primary hover:underline"
                >Home</router-link
              >
              <template v-if="section">
                <span class="text-gray-400">/</span>
                <router-link
                  :to="`/support-portal/topics/${section.id}`"
                  class="font-semibold text-primary hover:underline"
                  >{{ section.name }}</router-link
                >
              </template>
              <span class="text-gray-400">/</span>
              <span class="text-gray-700">{{ article.title }}</span>
            </div>
            <button
              type="button"
              @click="copyLink"
              class="font-semibold text-gray-500 hover:text-primary"
            >
              {{ linkCopied ? "Copied!" : "Copy link" }}
            </button>
          </div>

          <h1 class="mb-2 text-3xl font-extrabold leading-tight text-gray-900">
            {{ article.title }}
          </h1>
          <p v-if="lastUpdated" class="mb-7 text-sm text-gray-500">
            Last updated {{ lastUpdated }}
          </p>

          <div
            id="support-portal-article-content"
            v-html="article.body"
            class="article-body prose max-w-none mb-10"
          ></div>

          <div v-if="relatedArticles.length" class="mb-10">
            <span
              class="text-xs font-bold tracking-wide text-gray-700 uppercase"
              >Related articles</span
            >
            <div class="flex flex-col gap-2 mt-3">
              <ArticleListItem
                v-for="related in relatedArticles"
                :key="related.id"
                :title="related.title"
                :to="`/support-portal/articles/${related.id}`"
                compact
              />
            </div>
          </div>

          <div
            class="flex flex-col items-center gap-4 p-6 text-center rounded-xl bg-primary-50 sm:flex-row sm:text-left"
          >
            <div class="flex-1">
              <p class="mb-1 font-bold text-gray-900">Still need help?</p>
              <p class="text-sm text-gray-600">
                Our team is available Monday – Friday, 8am – 5pm MST.
              </p>
            </div>
            <div class="flex flex-wrap justify-center gap-3 sm:justify-end">
              <a
                href="tel:8887221223"
                class="px-5 py-2 text-sm font-bold text-white rounded-full bg-primary hover:bg-primary-mid transition-colors whitespace-nowrap"
                >Call 888.722.1223</a
              >
              <a
                href="mailto:info@rmrbenefits.com"
                class="px-5 py-2 text-sm font-bold text-gray-700 bg-white border border-gray-300 rounded-full hover:border-primary hover:text-primary transition-colors whitespace-nowrap"
                >Send an email</a
              >
            </div>
          </div>
        </div>
      </div>
    </div>
  </base-view>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import BaseView from "@/components/layout/BaseView.vue";
import ArticleListItem from "./components/ArticleListItem.vue";

export default {
  name: "SupportRedesignArticle",
  components: { BaseView, ArticleListItem },
  data() {
    return {
      articleId: parseInt(this.$route.params.articleId),
      linkCopied: false,
      linkCopiedTimer: null,
      prevRoute: null
    };
  },
  beforeRouteEnter(to, from, next) {
    next(vm => {
      vm.prevRoute = from;
    });
  },
  watch: {
    $route(to) {
      this.articleId = parseInt(to.params.articleId);
    },
    article: {
      immediate: true,
      handler(article) {
        this.$nextTick(() => {
          this.markExternalLinks();
        });
        if (article) {
          document.title = `${article.title} | Support | Rocky Mountain Reserve`;
        }
      }
    }
  },
  mounted() {
    this._anchorHandler = e => {
      const link = e.target.closest("a");
      if (link && link.hash && link.hash.startsWith("#h_")) {
        e.preventDefault();
        const target = document.getElementById(link.hash.substring(1));
        if (target) target.scrollIntoView({ behavior: "smooth" });
      }
    };
    document.addEventListener("click", this._anchorHandler);
  },
  beforeDestroy() {
    document.removeEventListener("click", this._anchorHandler);
    clearTimeout(this.linkCopiedTimer);
  },
  computed: {
    ...mapGetters(["allZendeskData", "zendeskError"]),
    loading() {
      return !(this.allZendeskData && this.allZendeskData.articles);
    },
    article() {
      return this.$store.getters.articleById(this.articleId);
    },
    section() {
      if (!this.article) return null;
      return this.$store.getters.sectionById(this.article.section_id);
    },
    relatedArticles() {
      if (
        !this.article ||
        !this.allZendeskData ||
        !this.allZendeskData.articles
      )
        return [];
      const labels = new Set(
        (this.article.label_names || []).filter(
          l => !l.startsWith("subsection:")
        )
      );
      if (!labels.size) {
        return (this.section ? this.section.articles || [] : [])
          .filter(a => a.id !== this.article.id)
          .slice(0, 3);
      }
      return this.allZendeskData.articles
        .filter(a => {
          if (a.id === this.article.id) return false;
          return (a.label_names || []).some(l => labels.has(l));
        })
        .slice(0, 3);
    },
    backLabel() {
      if (!this.prevRoute || !this.prevRoute.name) return null;
      const name = this.prevRoute.name;
      if (name === "Support Redesign - Home") {
        return this.prevRoute.query && this.prevRoute.query.q
          ? "Back to search results"
          : null;
      }
      if (name === "Support Redesign - Topic") return "Back to topic";
      return null;
    },
    lastUpdated() {
      if (!this.article || !this.article.edited_at) return null;
      return new Date(this.article.edited_at).toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric"
      });
    }
  },
  methods: {
    ...mapActions(["fetchZendeskData"]),
    copyLink() {
      if (!navigator.clipboard) return;
      navigator.clipboard.writeText(window.location.href).then(() => {
        this.linkCopied = true;
        clearTimeout(this.linkCopiedTimer);
        this.linkCopiedTimer = setTimeout(() => {
          this.linkCopied = false;
        }, 2000);
      });
    },
    markExternalLinks() {
      const container = document.getElementById(
        "support-portal-article-content"
      );
      if (!container) return;
      container.querySelectorAll("a[href]").forEach(link => {
        if (link.hash && link.hash.startsWith("#h_")) return;
        if (link.hostname && link.hostname !== window.location.hostname) {
          link.setAttribute("target", "_blank");
          link.setAttribute("rel", "noopener");
        }
      });
    }
  }
};
</script>

<style scoped>
/* Matches base-view's own min-height so the background fills the page even
   when there's little content, instead of the gray-lighter wrapper
   stopping short and showing plain white below. */
.page-fill {
  min-height: calc(100vh - 10rem);
}
</style>

<style>
#support-portal-article-content img {
  width: 100%;
  height: auto;
  max-width: 40rem;
}

#support-portal-article-content iframe {
  border-radius: 0.25rem;
  border: 1px solid gainsboro;
  width: 100%;
  max-width: 19rem;
}

#support-portal-article-content a {
  color: #096843;
  text-decoration: underline;
}

#support-portal-article-content ul,
#support-portal-article-content ol {
  padding-left: 1.375rem;
}

/* Zendesk's WYSIWYG editor writes its own utility classes into article
   bodies (font size, alignment, indent, tables, list styling, etc). Prose
   handles baseline typography, but these classes need explicit rules or
   they render as plain, unstyled markup. */
#support-portal-article-content .wysiwyg-font-size-small {
  font-size: small;
}

#support-portal-article-content .wysiwyg-font-size-large {
  font-size: large;
}

#support-portal-article-content .wysiwyg-font-size-x-large {
  font-size: x-large;
}

#support-portal-article-content .wysiwyg-underline {
  text-decoration: underline;
}

#support-portal-article-content .wysiwyg-text-align-center {
  text-align: center;
}

#support-portal-article-content .wysiwyg-text-align-right {
  text-align: right;
}

#support-portal-article-content .wysiwyg-indent1 {
  padding-left: 2.5rem;
}

#support-portal-article-content .wysiwyg-indent2 {
  padding-left: 5rem;
}

#support-portal-article-content .wysiwyg-indent3 {
  padding-left: 7.5rem;
}

#support-portal-article-content .wysiwyg-indent4 {
  padding-left: 10rem;
}

#support-portal-article-content .wysiwyg-list-bold {
  font-weight: 700;
}

#support-portal-article-content .wysiwyg-list-color::marker {
  color: var(--wysiwyg-list-marker-color);
}

#support-portal-article-content .ck-list-marker-italic::marker {
  font-style: italic;
}

#support-portal-article-content .wysiwyg-image,
#support-portal-article-content .wysiwyg-image-resized,
#support-portal-article-content .wysiwyg-media {
  display: block;
  margin: 0 auto;
}

/* Zendesk only sets border-color inline on table cells, so tables need a
   border-width/style (and a fallback color) to actually render visibly. */
#support-portal-article-content table {
  border-collapse: collapse;
  width: 100%;
  margin-bottom: 1.5rem;
}

#support-portal-article-content .wysiwyg-table-align-left {
  margin-right: auto;
}

#support-portal-article-content th,
#support-portal-article-content td {
  border: 1px solid #d1d5db;
  padding: 0.5rem 0.75rem;
  text-align: left;
  vertical-align: top;
}

#support-portal-article-content thead {
  background: #f9fafb;
}
</style>
